import React from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {UserData} from "types/User";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import UserSettingsForm from "../Form";
import {FormikHelpers} from "formik";

function MutateUserSettings() {
  const auth = useAuth();
  const {notify} = useNotifications();

  const currentUserId = auth.user?.id;

  const {data: user, isLoading} = useQuery(
    ['users', {id: currentUserId ?? 0}],
    () => ngage.users.get(currentUserId ?? 0),
    {enabled: auth.can('users:read:own') && !!currentUserId}
  )

  const update = useMutation((data: UserData) => ngage.users.update(currentUserId ?? 0, data));

  // Prevent unauthorised mutating
  if (auth.cannot('users:write:own')) {
    return <NotAuthorised/>
  }

  // Handle form submission
  async function handleSubmit(values: UserData, formik: FormikHelpers<UserData>) {
    await handleUpdate(values);

    // Reset the profile_picture_id field
    void formik.setFieldValue('profile_picture_id', undefined);

    await invalidate('users.operator', { refetchInactive: true });
    await auth.refetchCurrentUser();
  }

  // Handle update mutation
  async function handleUpdate(values: UserData) {
    try {
      await update.mutateAsync(values)
      notify("success", "User settings", `Your user settings have been updated`)
      await invalidate(['users', {id: currentUserId}])
    } catch (error) {
      notify("error", "User settings", `Your user settings could not be updated`)
      throw error
    }
  }

  return (
    <Page
      title='User Settings'
      isLoading={isLoading}
    >
      {user && <UserSettingsForm user={user} onSubmit={handleSubmit}/>}
    </Page>
  );
}

export default MutateUserSettings;